// src/components/ProductList.jsx
"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { hydrateProductDetails } from "@/features/landing/utils/hydrateProductDetails";
import { formatProduct } from "@/features/landing/utils/formatProduct";
import ProductCard from "@/components/ProductCard";

export default function ProductList({
  title,
  products,
  selectedProduct,
  onSelect,
  page = 1,
  totalPages = 1,
  onPageChange,

  // optional (some callers pass these)
  user,
  piRate,
  feePercent,

  // ✅ NEW: allow your sections to control layout
  variant = "list",
  columns = "",
  gap = "",
}) {
  const [loadingDetail, setLoadingDetail] = useState(false);

  const piReady = Number.isFinite(piRate) && piRate > 0;

  const formattedProducts = useMemo(() => {
    return (products || []).map((p) => {
      const formatted = formatProduct(p, user, {
        piRate: piReady ? piRate : undefined,
        feePercent,
      });

      return {
        ...p,
        ...formatted,
        brandName: p.brandName ?? formatted.brandName,
        uiBrand: p.uiBrand ?? formatted.uiBrand,
        uiTitle: p.uiTitle ?? formatted.uiTitle,
      };
    });
  }, [products, user, piReady, piRate, feePercent]);

  // keep latest onSelect in a ref
  const onSelectRef = useRef(onSelect);
  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  const selId = selectedProduct?.offerId ?? selectedProduct?.sku ?? null;
  const selType = String(
    selectedProduct?.productType || selectedProduct?.type || ""
  ).toUpperCase();

  const lastHydratedKeyRef = useRef(null);

  useEffect(() => {
    if (!selId) return;

    // compute hydration need without depending on whole object
    const hasPriceBlock = !!(
      selectedProduct?.price?.currency &&
      (selectedProduct?.price?.fixed ??
        selectedProduct?.price?.min ??
        selectedProduct?.priceFixed ??
        selectedProduct?.priceMin)
    );

    const hasFaceBlock = !!(
      selectedProduct?.send?.currency &&
      (selectedProduct?.send?.fixed ??
        selectedProduct?.send?.min ??
        selectedProduct?.sendFixed ??
        selectedProduct?.sendMin)
    );

    const hasDetailsBlock =
      Array.isArray(selectedProduct?.details) && selectedProduct.details.length > 0;

    const hasRedemption =
      Array.isArray(selectedProduct?.redemptionInstructions) &&
      selectedProduct.redemptionInstructions.length > 0;

    const needsHydrate =
      !hasPriceBlock ||
      (selType === "VOUCHER" && (!hasFaceBlock || !hasDetailsBlock || !hasRedemption));

    if (!needsHydrate) return;

    const key = `${selType}:${selId}`;
    if (lastHydratedKeyRef.current === key) return;
    lastHydratedKeyRef.current = key;

    const controller = new AbortController();
    let cancelled = false;
    setLoadingDetail(true);

    hydrateProductDetails(
      { offerId: selId, productType: selType.toLowerCase() },
      { signal: controller.signal }
    )
      .then((data) => {
        if (cancelled) return;
        if (data?.offerId && data.offerId !== selId) return;

        const isFixedPrice = data?.priceType === "FIXED";

        onSelectRef.current?.({
          ...selectedProduct,
          ...data,
          description:
            data?.description ??
            data?.fullDetail?.description ??
            selectedProduct?.description ??
            "",
          price: data?.price ?? data?.fullDetail?.price ?? selectedProduct?.price,
          send: data?.send ?? data?.fullDetail?.send ?? selectedProduct?.send,
          details: data?.details ?? data?.fullDetail?.details ?? selectedProduct?.details,
          redemptionInstructions:
            data?.redemptionInstructions ??
            data?.redemption_instructions ??
            data?.fullDetail?.redemptionInstructions ??
            selectedProduct?.redemptionInstructions ??
            [],
          brandInfoPdf:
            data?.brandInfoPdf ??
            data?.fullDetail?.brandInfoPdf ??
            selectedProduct?.brandInfoPdf ??
            "",
          requiredFields: data?.required_fields || data?.requiredFields || [],
          isFixedPrice,
        });
      })
      .catch((err) => {
        if (cancelled || err?.name === "AbortError") return;
        lastHydratedKeyRef.current = null;
        console.warn(`hydrate-failed [${selId}]`, err);
      })
      .finally(() => {
        if (!cancelled) setLoadingDetail(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
    // ✅ depend only on id/type + the few fields we inspected
  }, [
    selId,
    selType,
    selectedProduct?.price,
    selectedProduct?.send,
    selectedProduct?.details,
    selectedProduct?.redemptionInstructions,
  ]);

  const listClass =
    variant === "compact"
      ? `grid ${columns || "grid-cols-1 sm:grid-cols-2 md:grid-cols-3"} ${gap || "gap-3"}`
      : "space-y-2";

  return (
    <section className="mb-6 text-textPrimary">
      {title && <h2 className="text-xl font-semibold mb-4">{title}</h2>}

      <ul className={listClass}>
        {formattedProducts.map((product) => (
          <li key={product.offerId || product.sku}>
          <ProductCard
          {...product}
          piRate={piReady ? piRate : undefined}
          feePercent={feePercent}
          piPending={!piReady}
          showCryptoHint
          onClick={() => onSelect?.(product)}
        />
          </li>
        ))}
      </ul>

      {totalPages > 1 && (
        <div className="flex justify-between items-center mt-5 text-sm">
          <button
            type="button"
            className="inline-flex items-center min-h-[44px] px-4 rounded-md bg-surface hover:bg-primary/10 border border-border disabled:opacity-50"
            disabled={page === 1}
            onClick={() => onPageChange?.(page - 1)}
          >
            ← Previous
          </button>

          <span className="text-textSecondary">
            Page <strong>{page}</strong> of {totalPages}
          </span>

          <button
            type="button"
            className="inline-flex items-center min-h-[44px] px-4 rounded-md bg-surface hover:bg-primary/10 border border-border disabled:opacity-50"
            disabled={page === totalPages}
            onClick={() => onPageChange?.(page + 1)}
          >
            Next →
          </button>
        </div>
      )}

      {loadingDetail && selectedProduct && (
        <div className="mt-6 p-4 rounded-xl border border-border bg-surface text-sm text-textSecondary animate-pulse">
          Loading details for {selectedProduct.brandName || selectedProduct.name}...
        </div>
      )}
    </section>
  );
}
